/**
 * Frontend API path centralization — single source of truth.
 *
 * 与 `docs/openapi.yaml` 双校验：新增 / 修改 / 弃用任何 endpoint，
 * 必须同时改 server/internal/<module>/handler.go 的 g.POST/GET 注册名
 * 和本文件的 `apiPaths.<group>.<key>`；所有 features 下的 api/*.ts 一律从这里
 * import，禁止再出现裸的 API v1 字面量。
 *
 * 设计原则：
 *  - 顶层常量 `API_V1` = '/api/v1'，所有路径从此衍生；不要硬编码前缀。
 *  - 路径按业务域分组，键名与后端 handler.go 的 RegisterRoutes 注册名对齐
 *    （photoMemories ← server/internal/photomem/handler.go，
 *     meetStory    ← server/internal/meetstory/handler.go，
 *     letters      ← server/internal/letter/handler.go，
 *     spaces       ← server/internal/space/handler.go，
 *     me / auth    ← server/internal/auth/handler.go，
 *     search       ← GET 查询）。
 *  - 含动态 id 的路径必须用 builder 函数；调用方拿到的是已 encode 的字符串，
 *    不允许再拼接 `/` 或 `${id}`，防止 path traversal / 双斜杠。
 *  - 全部 `as const`，TS 推断为精确字面量类型，编译期就能发现错路径。
 *  - `ApiPath` 联合类型供其他模块引用，可作参数约束 / 测试断言。
 */

const API_V1 = '/api/v1' as const;

/**
 * 对单个动态段做 encodeURIComponent；保留前缀 `/` 拼接。
 * 严禁把整段含 `/` 的字符串丢进来——只对叶子 id 做 escape，
 * path traversal 必须在 build 阶段堵掉，不依赖后端鉴权。
 */
const encodeId = (id: string): string => encodeURIComponent(id);

export const apiPaths = {
  /**
   * 鉴权 / 账号会话（server/internal/auth/handler.go）。
   * public + protected 都在这里；Bearer 头由 ApiClient 注入，
   * 这里只负责 URL。
   */
  auth: {
    register: `${API_V1}/auth/register`,
    login: `${API_V1}/auth/login`,
    logout: `${API_V1}/auth/logout`,
    refresh: `${API_V1}/auth/refresh`,
    smsRequest: `${API_V1}/auth/sms/request`,
    smsLogin: `${API_V1}/auth/sms/login`,
    passwordResetRequest: `${API_V1}/auth/password-reset/request`,
    passwordResetConfirm: `${API_V1}/auth/password-reset/confirm`,
  },

  /**
   * 当前账号子树（server/internal/auth/handler.go priv group）。
   * 所有路径必须 Bearer。
   */
  me: {
    base: `${API_V1}/me`,
    achievements: `${API_V1}/me/achievements`,
    displayName: `${API_V1}/me/display-name`,
    avatar: `${API_V1}/me/avatar`,
    password: `${API_V1}/me/password`,
    keys: `${API_V1}/me/keys`,
  },

  /**
   * 双人空间（server/internal/space/handler.go）。
   * `current` / `members` bound-only；`role` 任意已登录；`leave` 任意已登录。
   */
  spaces: {
    current: `${API_V1}/spaces/current`,
    leave: `${API_V1}/spaces/leave`,
    members: `${API_V1}/spaces/members`,
    role: `${API_V1}/spaces/role`,
    candidates: `${API_V1}/spaces/candidates`,
    invitations: `${API_V1}/spaces/invitations`,
    invitation: (id: string): string => `${API_V1}/spaces/invitations/${encodeId(id)}`,
    invitationRespond: (id: string): string => `${API_V1}/spaces/invitations/${encodeId(id)}/respond`,
    invitationConfirm: (id: string): string => `${API_V1}/spaces/invitations/${encodeId(id)}/confirm`,
  },

  /**
   * 时间相册 photo_memory（server/internal/photomem/handler.go）。
   * 所有路径 bound-only；删除/编辑他人内容可能回 'pending_*' 或 4xx。
   */
  photoMemories: {
    /** GET / POST 主资源（list 默认仅 published 视图） */
    base: `${API_V1}/photo-memories`,
    /** PATCH / DELETE /api/v1/photo-memories/:id */
    byId: (id: string): string => `${API_V1}/photo-memories/${encodeId(id)}`,
    publish: (id: string): string => `${API_V1}/photo-memories/${encodeId(id)}/publish`,
    restore: (id: string): string => `${API_V1}/photo-memories/${encodeId(id)}/restore`,
    purge: (id: string): string => `${API_V1}/photo-memories/${encodeId(id)}/purge`,
    /** POST /api/v1/photo-memories/:id/attachments */
    attachments: (id: string): string =>
      `${API_V1}/photo-memories/${encodeId(id)}/attachments`,
    /** DELETE /api/v1/photo-memories/:id/attachments/:attId */
    attachmentById: (id: string, attId: string): string =>
      `${API_V1}/photo-memories/${encodeId(id)}/attachments/${encodeId(attId)}`,
    /** GET /api/v1/photo-memories/:id/attachments/:attId/raw */
    attachmentRaw: (id: string, attId: string): string =>
      `${API_V1}/photo-memories/${encodeId(id)}/attachments/${encodeId(attId)}/raw`,
  },

  /**
   * 《相识相遇》meet_story（server/internal/meetstory/handler.go）。
   * 主资源任意成员可读、owner 可写；milestones 全部 owner-only。
   */
  meetStory: {
    /** GET / PATCH 主体（无 id） */
    base: `${API_V1}/meet-story`,
    attachments: `${API_V1}/meet-story/attachments`,
    attachmentById: (id: string): string => `${API_V1}/meet-story/attachments/${encodeId(id)}`,
    attachmentRaw: (id: string): string => `${API_V1}/meet-story/attachments/${encodeId(id)}/raw`,
    /** POST /api/v1/meet-story/milestones */
    milestones: `${API_V1}/meet-story/milestones`,
    /**
     * POST /api/v1/meet-story/milestones/reorder
     * 必须在 `milestonesById` 之前注册（gin 静态段优先），
     * 路径字面量与 handler.go 保持一致以利 review。
     */
    milestonesReorder: `${API_V1}/meet-story/milestones/reorder`,
    /** PATCH / DELETE /api/v1/meet-story/milestones/:id */
    milestonesById: (id: string): string =>
      `${API_V1}/meet-story/milestones/${encodeId(id)}`,
  },

  /**
   * 《那些话》letters（server/internal/letter/handler.go）。
   * 草稿与已发布共用同一条资源，差异在 `?tab=drafts` query / status 字段。
   */
  letters: {
    /** GET / POST 主资源（list 默认仅 published） */
    base: `${API_V1}/letters`,
    /** GET /api/v1/letters?tab=drafts —— 草稿列表，query 形式，非 REST 子资源 */
    drafts: `${API_V1}/letters?tab=drafts`,
    /** GET / PATCH / DELETE /api/v1/letters/:id */
    byId: (id: string): string => `${API_V1}/letters/${encodeId(id)}`,
    /** POST /api/v1/letters/:id/publish */
    publishById: (id: string): string =>
      `${API_V1}/letters/${encodeId(id)}/publish`,
    restore: (id: string): string => `${API_V1}/letters/${encodeId(id)}/restore`,
    purge: (id: string): string => `${API_V1}/letters/${encodeId(id)}/purge`,
  },

  /** 回收站聚合列表（server/internal/recycle/handler.go）。 */
  recycleBin: {
    base: `${API_V1}/recycle-bin`,
    detail: (kind: string, id: string): string => `${API_V1}/recycle-bin/${encodeId(kind)}/${encodeId(id)}`,
  },

  /**
   * 全局搜索（GET，query string）。
   * 后端按当前空间和账号权限过滤照片回忆与信件。
   */
  search: {
    base: `${API_V1}/search`,
  },

  /**
   * 客户端遥测（server/internal/clientlog/handler.go）。
   * 匿名、高频；每次失败都向这里静默上报，后端落 client_logs 表
   * 并按 60s 窗口错误率触发 zap.Error 告警。SPA 自己负责节流。
   */
  clientLogs: {
    base: `${API_V1}/client-logs`,
  },

  /**
   * 客户端操作日志 operation_logs（server/internal/operationlog/handler.go）。
   * - `list`   : owner-only GET，response 命中 403 由上层 RBAC 处理。
   * - `ingest` : 任意已登录用户 POST，自家前端 SPA 用于上报用户行为埋点。
   * SPA 自己负责批处理 / 节流 / 去重；服务端落 operation_logs 表。
   */
  operationLogs: {
    base: `${API_V1}/operation-logs`,
    list: `${API_V1}/operation-logs`,
    ingest: `${API_V1}/operation-logs`,
  },

  /**
   * 客户端请求日志 request_logs（server/internal/requestlog/handler.go）。
   * - `list`   : owner-only GET，response 命中 403 由上层 RBAC 处理。
   * - `ingest` : 任意已登录用户 POST，按 `batch_id` 聚合落 request_logs 表。
   * 与 operationLogs 同样的 SPA 端节流约束；M4 才会挂埋点。
   */
  requestLogs: {
    base: `${API_V1}/request-logs`,
    list: `${API_V1}/request-logs`,
    ingest: `${API_V1}/request-logs`,
  },

  /** 通知中心（server/internal/notification/handler.go）。 */
  notifications: {
    base: `${API_V1}/notifications`,
    unreadCount: `${API_V1}/notifications/unread-count`,
    markRead: (id: string): string => `${API_V1}/notifications/${encodeId(id)}/read`,
    readAll: `${API_V1}/notifications/read-all`,
  },

  /**
   * 管理端 owner-only（server/internal/admin/handler.go）。
   * 仅 owner 角色可访问；前端按钮按 SpaceRole 隐藏但服务端仍按 rbac.md §5.2 校验。
   * `settings` 读写当前空间的 start_date / description / 配额与自动审批策略；
   * `members` 复用 spaces 视图，前端可独立路径引用以表达"管理视角"。
   */
  admin: {
    /** GET / PUT /api/v1/admin/settings */
    settings: `${API_V1}/admin/settings`,
    /** GET /api/v1/admin/members —— 与 spaces.members 同源，admin 视角 */
    members: `${API_V1}/admin/members`,
  },

  /**
   * 删除 / 编辑申请 delete_requests（server/internal/deletereq/handler.go）。
   * - `base`      : 我发起的申请列表（GET /api/v1/delete-requests）
   * - `inbox`     : 我收到的待审批列表（GET /api/v1/delete-requests/inbox）
   * - `byId(id)`  : 单条详情（GET /api/v1/delete-requests/:id）
   * - `approve` / `reject` / `cancel` : 目标资源 owner 才能 approve / reject；
   *                                     发起人本人才能 cancel；reject 可带 reason。
   */
  deleteRequests: {
    base: `${API_V1}/delete-requests`,
    inbox: `${API_V1}/delete-requests/inbox`,
    byId: (id: string): string => `${API_V1}/delete-requests/${encodeId(id)}`,
    approve: (id: string): string =>
      `${API_V1}/delete-requests/${encodeId(id)}/approve`,
    reject: (id: string): string =>
      `${API_V1}/delete-requests/${encodeId(id)}/reject`,
    cancel: (id: string): string =>
      `${API_V1}/delete-requests/${encodeId(id)}/cancel`,
  },

  /**
   * 审批中心 approvals（server/internal/approvals/handler.go）。
   * Phase 3 升级后的统一入口，覆盖 photo_memory / letter / meet_story_milestone：
   *  - `base`    : POST /api/v1/approvals 创建 / GET 列表（我发起的见请求）
   *  - `inbox`   : GET /api/v1/approvals/inbox 我收到的待审批（owner 视角）
   *  - `byId(id)`: GET /api/v1/approvals/:id 单条详情
   *  - `approve` / `reject` / `cancel` : owner 才能 approve/reject，requester 才能 cancel
   * 跨 owner PATCH/DELETE 由 Phase 2 后端以 HTTP 202 + pending_approval 形式返回，
   * 前端 catch 分支（status === 202）走 inbound `PendingApprovalError`」。
   */
  approvals: {
    base: `${API_V1}/approvals`,
    inbox: `${API_V1}/approvals/inbox`,
    byId: (id: string): string => `${API_V1}/approvals/${encodeId(id)}`,
    approve: (id: string): string =>
      `${API_V1}/approvals/${encodeId(id)}/approve`,
    reject: (id: string): string =>
      `${API_V1}/approvals/${encodeId(id)}/reject`,
    cancel: (id: string): string =>
      `${API_V1}/approvals/${encodeId(id)}/cancel`,
  },

  /** 首页测试图集（服务端从 OSS 读取，不写入 SQLite）。 */
  homeAssets: {
    manifest: `${API_V1}/home-assets/manifest`,
    raw: (name: string): string => `${API_V1}/home-assets/${encodeId(name)}/raw`,
  },
  // Phase 8: BFF 聚合端点(由 bff/src/routes/bff/* 实现,server 不暴露)。
  // 前端 Phase 9+ 才会开始调用;本阶段仅保证 manifest / paths.ts / openapi
  // 三方一致,以便 schema-consistency 反向断言不报错。
  bff: {
    home: '/api/bff/home',
    meSummary: '/api/bff/me/summary',
  },
} as const;

/**
 * 路径联合类型；可作为参数约束 / 测试断言使用：
 *   const x: ApiPath['letters']['byId'] = apiPaths.letters.byId; // 编译通过
 *   const y: ApiPath['auth']['login'] = apiPaths.auth.login;     // 编译通过
 * 编译时即可断言调用方拿到了正确的 builder。
 */
export type ApiPath = typeof apiPaths;
